import type { ReactNode } from 'react'

import { cn } from '../utilitarios/cn'
import { Caixa } from './caixa'
import { EstadoErro, EstadoVazio } from './estados'
import { Esqueleto } from './esqueleto'

export type AlinhamentoColuna = 'esquerda' | 'centro' | 'direita'

export interface ColunaTabela<T> {
  /** Também é o campo de ordenação enviado à API quando `ordenavel`. */
  chave: string
  cabecalho: ReactNode
  celula: (item: T) => ReactNode
  ordenavel?: boolean
  alinhamento?: AlinhamentoColuna
  /** Classe de largura/whitespace da célula (`w-40`, `whitespace-nowrap`). */
  classe?: string
  /** Esconde a coluna abaixo do breakpoint — a tabela larga cabe no notebook. */
  ocultarAte?: 'sm' | 'md' | 'lg' | 'xl'
}

export interface OrdenacaoTabela {
  campo: string
  direcao: 'asc' | 'desc'
}

export interface SelecaoTabela<T> {
  selecionados: ReadonlyArray<string>
  onAlterar: (selecionados: string[]) => void
  /** Itens não selecionáveis (lead terminal em transferência em lote). */
  bloqueado?: (item: T) => boolean
}

export interface PropriedadesTabelaDados<T> {
  colunas: ReadonlyArray<ColunaTabela<T>>
  itens: ReadonlyArray<T>
  chaveDe: (item: T) => string
  ordenacao?: OrdenacaoTabela
  onOrdenar?: (campo: string) => void
  selecao?: SelecaoTabela<T>
  carregando?: boolean
  erro?: ReactNode
  vazio?: ReactNode
  onClicarLinha?: (item: T) => void
  /** Realce de linha — o card rosa de lead atrasado da TELA-02/04. */
  realce?: (item: T) => boolean
  /**
   * Versão em cartão para telas estreitas. Quando informada, a tabela vira lista
   * de cartões abaixo de `md` — exigência de responsividade das telas de operação
   * (`01-screens.md`, "Requisito de responsividade").
   */
  cartaoMobile?: (item: T) => ReactNode
  rodape?: ReactNode
  className?: string
}

const ALINHAMENTO: Record<AlinhamentoColuna, string> = {
  esquerda: 'text-left',
  centro: 'text-center',
  direita: 'text-right',
}

const OCULTAR: Record<NonNullable<ColunaTabela<unknown>['ocultarAte']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
}

/**
 * Tabela de listagem — o "padrão de listagem" repetido em quase todas as telas
 * (`01-screens.md`).
 *
 * Ordenação e paginação são **do servidor**: `onOrdenar` devolve o campo e quem
 * chama refaz a consulta. Ordenar no cliente daria uma ordem diferente da que a
 * página seguinte traria, porque o cursor é keyset sobre a ordem do servidor.
 */
export function TabelaDados<T>({
  colunas,
  itens,
  chaveDe,
  ordenacao,
  onOrdenar,
  selecao,
  carregando,
  erro,
  vazio,
  onClicarLinha,
  realce,
  cartaoMobile,
  rodape,
  className,
}: PropriedadesTabelaDados<T>) {
  const selecionaveis = selecao
    ? itens.filter((item) => !selecao.bloqueado?.(item)).map(chaveDe)
    : []
  const todosMarcados =
    selecionaveis.length > 0 && selecionaveis.every((chave) => selecao?.selecionados.includes(chave))

  function alternarTodos() {
    if (!selecao) return
    selecao.onAlterar(todosMarcados ? [] : selecionaveis)
  }

  function alternarUm(chave: string) {
    if (!selecao) return
    selecao.onAlterar(
      selecao.selecionados.includes(chave)
        ? selecao.selecionados.filter((item) => item !== chave)
        : [...selecao.selecionados, chave],
    )
  }

  const totalColunas = colunas.length + (selecao ? 1 : 0)

  // String vira a moldura padrão de erro; nó pronto é renderizado como veio — quem
  // passa um `EstadoSemPermissao` ou um aviso de "endpoint indisponível" não quer
  // ver "Não foi possível carregar" em volta dele.
  const conteudoEspecial = erro ? (
    typeof erro === 'string' ? (
      <EstadoErro descricao={erro} />
    ) : (
      erro
    )
  ) : carregando ? (
    <div className="space-y-2 p-4">
      {Array.from({ length: 6 }, (_, indice) => (
        <Esqueleto key={indice} className="h-9 w-full" />
      ))}
    </div>
  ) : itens.length === 0 ? (
    (vazio ?? <EstadoVazio titulo="Nenhum registro encontrado" />)
  ) : null

  return (
    <div className={cn('flex flex-col', className)}>
      {conteudoEspecial ? (
        conteudoEspecial
      ) : (
        <>
          {/* Cartões — telas estreitas */}
          {cartaoMobile && (
            <ul className="flex flex-col gap-2 p-3 md:hidden">
              {itens.map((item) => (
                <li key={chaveDe(item)}>{cartaoMobile(item)}</li>
              ))}
            </ul>
          )}

          <div className={cn('overflow-x-auto', cartaoMobile && 'hidden md:block')}>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-borda bg-superficie-2">
                  {selecao && (
                    <th scope="col" className="w-10 px-3 py-2.5">
                      <Caixa
                        checked={todosMarcados}
                        onChange={alternarTodos}
                        aria-label="Selecionar todos os registros da página"
                      />
                    </th>
                  )}
                  {colunas.map((coluna) => {
                    const ativa = ordenacao?.campo === coluna.chave
                    return (
                      <th
                        key={coluna.chave}
                        scope="col"
                        aria-sort={
                          ativa
                            ? ordenacao.direcao === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : undefined
                        }
                        className={cn(
                          'px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-conteudo-suave',
                          ALINHAMENTO[coluna.alinhamento ?? 'esquerda'],
                          coluna.ocultarAte && OCULTAR[coluna.ocultarAte],
                          coluna.classe,
                        )}
                      >
                        {coluna.ordenavel && onOrdenar ? (
                          <button
                            type="button"
                            onClick={() => onOrdenar(coluna.chave)}
                            className={cn(
                              'inline-flex items-center gap-1 rounded hover:text-conteudo',
                              ativa && 'text-marca-600',
                            )}
                          >
                            {coluna.cabecalho}
                            <SetaOrdenacao ativa={ativa} direcao={ordenacao?.direcao} />
                          </button>
                        ) : (
                          coluna.cabecalho
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => {
                  const chave = chaveDe(item)
                  const marcado = selecao?.selecionados.includes(chave) ?? false
                  return (
                    <tr
                      key={chave}
                      onClick={onClicarLinha ? () => onClicarLinha(item) : undefined}
                      className={cn(
                        'border-b border-borda/70 transition-colors last:border-b-0',
                        realce?.(item) ? 'bg-perigo-suave' : 'bg-superficie',
                        marcado && 'bg-marca-50',
                        onClicarLinha && 'cursor-pointer hover:bg-superficie-2',
                      )}
                    >
                      {selecao && (
                        <td className="px-3 py-2.5" onClick={(evento) => evento.stopPropagation()}>
                          <Caixa
                            checked={marcado}
                            disabled={selecao.bloqueado?.(item)}
                            onChange={() => alternarUm(chave)}
                            aria-label="Selecionar registro"
                          />
                        </td>
                      )}
                      {colunas.map((coluna) => (
                        <td
                          key={coluna.chave}
                          className={cn(
                            'px-3 py-2.5 align-middle text-conteudo',
                            ALINHAMENTO[coluna.alinhamento ?? 'esquerda'],
                            coluna.ocultarAte && OCULTAR[coluna.ocultarAte],
                            coluna.classe,
                          )}
                        >
                          {coluna.celula(item)}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
              {rodape && (
                <tfoot>
                  <tr className="border-t border-borda bg-superficie-2">
                    <td colSpan={totalColunas} className="px-3 py-2">
                      {rodape}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function SetaOrdenacao({ ativa, direcao }: { ativa: boolean; direcao?: 'asc' | 'desc' }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className={cn('h-3 w-3 shrink-0', ativa ? 'opacity-100' : 'opacity-35')}
      fill="currentColor"
    >
      <path
        d="M6 1.5 9 5H3l3-3.5Z"
        className={ativa && direcao === 'desc' ? 'opacity-25' : undefined}
      />
      <path
        d="M6 10.5 3 7h6l-3 3.5Z"
        className={ativa && direcao === 'asc' ? 'opacity-25' : undefined}
      />
    </svg>
  )
}
